import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import { CreditCard, Sparkles, Zap, CheckCircle2, IndianRupee, RefreshCw, RotateCw, XCircle, Calendar, FileText, ExternalLink, History, Gift, Unlock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const INVOICE_STATUS_STYLES = {
  captured: 'bg-emerald-100 text-emerald-800',
  refunded: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  signature_failed: 'bg-red-100 text-red-800',
};

const getInvoiceStatusColor = (status) =>
  INVOICE_STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';

const getInvoiceDescription = (o) => {
  if (o.type === 'plan') return `${o.meta?.plan_name || 'Plan'} subscription`;
  if (o.type === 'credits') return `${o.meta?.credits || 0} lead credits`;
  return o.description || o.type;
};

const VendorBillingPanel = ({ token, onPlanUpdated }) => {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [plans, setPlans] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [credits, setCredits] = useState(10);
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // plan_id or 'credits'
  const [cancelling, setCancelling] = useState(null);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, p, me, subs, inv, hist] = await Promise.all([
        axios.get(`${API}/payments/config`).then((r) => r.data),
        axios.get(`${API}/plans`).then((r) => r.data),
        axios.get(`${API}/vendor/me`, auth()).then((r) => r.data).catch(() => null),
        axios.get(`${API}/payments/vendor/subscriptions`, auth()).then((r) => r.data).catch(() => ({ items: [] })),
        axios.get(`${API}/payments/vendor/invoices`, auth()).then((r) => r.data).catch(() => ({ items: [] })),
        axios.get(`${API}/vendor/me/credit-history`, auth()).then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      setConfig(cfg);
      setPlans(p || []);
      setVendor(me);
      setSubscriptions(subs.items || []);
      setInvoices(inv.items || []);
      setLedger(hist.items || []);
    } catch (err) {
      toast({ title: 'Failed to load billing', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [auth, toast]);

  useEffect(() => { load(); }, [load]);

  const openCheckout = async (order, isSubscription = false) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast({ title: 'Could not load Razorpay Checkout', variant: 'destructive' });
      setPaying(null);
      return;
    }
    const options = {
      key: order.key_id,
      name: config?.company_name || 'TelanganaMart',
      image: config?.company_logo_url || undefined,
      description: order.description,
      prefill: {
        name: order.vendor?.name || vendor?.name || '',
        email: order.vendor?.email || vendor?.email || '',
        contact: order.vendor?.mobile || vendor?.mobile || '',
      },
      theme: { color: '#ee5e23' },
      modal: {
        ondismiss: () => { setPaying(null); toast({ title: 'Payment cancelled' }); },
      },
      handler: async (resp) => {
        try {
          await axios.post(`${API}/payments/vendor/verify`, {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          }, auth());
          toast({ title: 'Payment successful', description: 'Your plan / credits have been applied.' });
          await load();
          // Notify the parent VendorDashboard so the header badge + other tabs see the new plan immediately
          if (typeof onPlanUpdated === 'function') {
            try {
              await onPlanUpdated();
            } catch (e) {
              // Non-fatal: payment already succeeded server-side; parent will refetch on next render.
              console.warn('Parent onPlanUpdated callback failed after checkout', e);
            }
          }
        } catch (err) {
          toast({ title: 'Verification failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
        } finally { setPaying(null); }
      },
    };
    if (isSubscription) {
      options.subscription_id = order.subscription_id;
    } else {
      options.order_id = order.order_id;
      options.amount = order.amount;
      options.currency = order.currency;
    }
    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (err) => {
      toast({ title: 'Payment failed', description: err?.error?.description || 'Please try again.', variant: 'destructive' });
      setPaying(null);
    });
    rzp.open();
  };

  const buyPlan = async (plan) => {
    if (!config?.enabled) {
      return toast({ title: 'Payments not configured', description: 'Admin must enable Razorpay in Site Settings first.', variant: 'destructive' });
    }
    setPaying(plan.id);
    try {
      const order = await axios.post(`${API}/payments/vendor/create-order`,
        { type: 'plan', plan_id: plan.id, recurring }, auth()).then((r) => r.data);
      openCheckout(order, !!order.is_subscription);
    } catch (err) {
      toast({ title: 'Could not create order', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      setPaying(null);
    }
  };

  const buyCredits = async () => {
    if (!config?.enabled) {
      return toast({ title: 'Payments not configured', variant: 'destructive' });
    }
    if (credits < 1) return toast({ title: 'Enter a credit quantity', variant: 'destructive' });
    setPaying('credits');
    try {
      const order = await axios.post(`${API}/payments/vendor/create-order`,
        { type: 'credits', credits }, auth()).then((r) => r.data);
      openCheckout(order);
    } catch (err) {
      toast({ title: 'Could not create order', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
      setPaying(null);
    }
  };

  const cancelSubscription = async (sub) => {
    const msg = `Cancel auto-renew for your ${sub.local_plan_id || 'subscription'}? You'll keep service until ${sub.current_end ? new Date(sub.current_end * 1000).toLocaleDateString('en-IN') : 'the end of the current billing cycle'}.`;
    if (!window.confirm(msg)) return;
    setCancelling(sub.id);
    try {
      await axios.post(`${API}/payments/vendor/subscriptions/${sub.id}/cancel?cancel_at_cycle_end=true`, {}, auth());
      toast({ title: 'Auto-renew cancelled', description: 'Service continues until the end of the current cycle.' });
      load();
    } catch (err) {
      toast({ title: 'Cancellation failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setCancelling(null); }
  };

  const fmtUnix = (sec) => sec ? new Date(sec * 1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const activeSubscription = subscriptions.find((s) => ['active', 'authenticated', 'pending'].includes(s.status));
  const cancellingSubscription = subscriptions.find((s) => s.cancel_at_cycle_end && s.status !== 'cancelled');

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading billing…</div>;

  return (
    <div className="space-y-6" data-testid="vendor-billing-panel">
      {/* Current plan + balance */}
      <div className="bg-gradient-to-r from-[#2c54b2] to-[#1f3f87] text-white rounded-lg p-5 flex items-center flex-wrap gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-wider text-blue-100">Current plan</div>
          <div className="text-2xl font-bold mt-1 flex items-center gap-2">
            {vendor?.plan_name || 'Silver'}
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded" style={{ background: vendor?.plan_color || '#9ca3af' }}>
              {vendor?.plan_name || 'Silver'}
            </span>
          </div>
          {vendor?.plan_expires_at && (
            <div className="text-xs text-blue-100 mt-1">Renews / expires: {new Date(vendor.plan_expires_at).toLocaleDateString('en-IN')}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-blue-100">Lead credits balance</div>
          <div className="text-3xl font-bold mt-1 flex items-center justify-end gap-1">
            <Zap className="w-6 h-6 text-yellow-300" /> {vendor?.lead_credits || 0}
          </div>
        </div>
      </div>

      {/* Active subscription banner */}
      {activeSubscription && !cancellingSubscription && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3" data-testid="subscription-active-banner">
          <div className="flex items-start gap-3">
            <RotateCw className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-emerald-900 inline-flex items-center gap-2">
                Auto-renew active
                <span className="text-[10px] uppercase font-bold tracking-wider bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded">{activeSubscription.status}</span>
              </div>
              <div className="text-xs text-emerald-800 mt-0.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Next charge: <strong>{fmtUnix(activeSubscription.charge_at) || '—'}</strong>
                {activeSubscription.paid_count != null && activeSubscription.total_count
                  ? <span className="text-emerald-700 ml-2">· Cycle {activeSubscription.paid_count + 1} of {activeSubscription.total_count}</span>
                  : null}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => cancelSubscription(activeSubscription)}
            disabled={cancelling === activeSubscription.id}
            className="text-red-700 border-red-200 hover:bg-red-50"
            data-testid="cancel-subscription-btn"
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            {cancelling === activeSubscription.id ? 'Cancelling…' : 'Cancel auto-renew'}
          </Button>
        </div>
      )}

      {/* Pending cancellation banner */}
      {cancellingSubscription && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="subscription-cancelling-banner">
          <XCircle className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>Auto-renew is cancelled.</strong> You&apos;ll keep service until <strong>{fmtUnix(cancellingSubscription.current_end) || 'the end of your current cycle'}</strong>. You can re-subscribe any time after that.
          </div>
        </div>
      )}

      {!config?.enabled && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-sm" data-testid="rzp-disabled-notice">
          Payments are <strong>not configured yet</strong>. An admin must add Razorpay keys in <em>Admin → Site Settings → Razorpay</em>.
        </div>
      )}

      {/* Plans */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#ee5e23]" /> Upgrade your plan
          </h2>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="recurring-toggle">
            <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
            <span>Auto-renew (Razorpay Subscription)</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {plans.map((p) => {
            const isCurrent = vendor?.plan_id === p.id;
            const isFree = (p.price || 0) === 0;
            return (
              <div key={p.id} className={`bg-white border-2 rounded-lg p-5 flex flex-col gap-3 ${isCurrent ? 'border-[#ee5e23]' : 'border-gray-200'}`} data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded text-white" style={{ background: p.color || '#9ca3af' }}>
                    {p.name}
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900 flex items-baseline gap-1">
                  {isFree ? 'Free' : <><IndianRupee className="w-5 h-5" />{Number(p.price).toLocaleString('en-IN')}</>}
                  {!isFree && <span className="text-xs text-gray-500 font-normal">/ {p.duration_days || 365} days</span>}
                </div>
                <ul className="text-xs text-gray-600 space-y-1">
                  {(p.features || []).slice(0, 5).map((f, i) => (
                    <li key={`${p.id}-feat-${i}-${(f || '').slice(0, 20)}`} className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <div className="mt-auto pt-3">
                  {isCurrent ? (
                    <Button disabled className="w-full bg-gray-100 text-gray-500" data-testid={`plan-current-${p.id}`}>Current plan</Button>
                  ) : isFree ? (
                    <Button disabled className="w-full" variant="outline">Default plan</Button>
                  ) : (
                    <Button
                      onClick={() => buyPlan(p)}
                      disabled={paying === p.id || !config?.enabled}
                      className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white"
                      data-testid={`buy-plan-${p.id}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {paying === p.id ? 'Opening checkout…' : `Upgrade to ${p.name}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credit top-up */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Zap className="w-5 h-5 text-yellow-500" /> Top-up lead credits
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {config?.credits_inr_per_lead
            ? <>₹{config.credits_inr_per_lead} per lead. Credits never expire.</>
            : 'Buy additional leads beyond your plan limit. Credits never expire.'}
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-gray-700 block">Number of leads</label>
            <Input
              type="number"
              min={1}
              value={credits}
              onChange={(e) => setCredits(Math.max(1, Number(e.target.value) || 0))}
              className="w-32"
              data-testid="credits-input"
            />
          </div>
          <div className="text-sm">
            <div className="text-gray-500 text-xs">Total</div>
            <div className="text-xl font-bold">₹{(credits * (config?.credits_inr_per_lead || 10)).toLocaleString('en-IN')}</div>
          </div>
          <Button
            onClick={buyCredits}
            disabled={paying === 'credits' || !config?.enabled || credits < 1}
            className="bg-[#2c54b2] hover:bg-[#1f3f87] text-white"
            data-testid="buy-credits-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {paying === 'credits' ? 'Opening checkout…' : 'Buy credits'}
          </Button>
        </div>
      </div>

      <Button variant="ghost" size="sm" onClick={load} className="text-xs">
        <RefreshCw className="w-3 h-3 mr-1" /> Refresh
      </Button>

      {/* Credit history */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="credit-history">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <History className="w-5 h-5 text-[#2c54b2]" /> Credit History
        </h2>
        <p className="text-xs text-gray-500 mb-4">Every credit you&apos;ve topped up, been gifted, or spent unlocking buy leads.</p>
        {ledger.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded border border-dashed border-gray-200" data-testid="credit-history-empty">
            No credit activity yet. Top up credits or unlock a buy lead to see entries here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date', 'Activity', 'Details', 'Credits'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.map((l) => {
                  const meta = {
                    topup: { label: 'Top-up', icon: Zap, cls: 'bg-blue-50 text-[#2c54b2] border-blue-200' },
                    welcome_bonus: { label: 'Welcome gift', icon: Gift, cls: 'bg-purple-50 text-purple-700 border-purple-200' },
                    admin_gift: { label: 'Bonus gift', icon: Gift, cls: 'bg-green-50 text-green-700 border-green-200' },
                    lead_unlock: { label: 'Lead unlock', icon: Unlock, cls: 'bg-orange-50 text-[#ee5e23] border-orange-200' },
                  }[l.type] || { label: l.type, icon: History, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
                  const Icon = meta.icon;
                  return (
                    <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`credit-history-row-${l.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate text-gray-600 text-xs">
                        {l.type === 'lead_unlock' ? (l.product || 'Buy lead')
                          : l.type === 'welcome_bonus' ? 'Signup bonus'
                          : l.type === 'admin_gift' ? (l.note || 'Goodwill gift from TelanganaMart')
                          : 'Razorpay payment'}
                      </td>
                      <td className={`px-3 py-2 whitespace-nowrap font-bold ${l.credits > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {l.credits > 0 ? `+${l.credits}` : l.credits}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment history / invoices */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="invoice-history">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-[#2c54b2]" /> Payment History &amp; Invoices
        </h2>
        <p className="text-xs text-gray-500 mb-4">Download or print receipts for every plan upgrade and credit top-up.</p>
        {invoices.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded border border-dashed border-gray-200" data-testid="invoices-empty">
            No payments yet. Your invoices will appear here after your first transaction.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2 text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((o) => {
                  const statusColor = getInvoiceStatusColor(o.status);
                  const desc = getInvoiceDescription(o);
                  return (
                    <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`invoice-row-${o.id}`}>
                      <td className="px-2 py-2 text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="px-2 py-2 text-xs">{desc}</td>
                      <td className="px-2 py-2 text-right font-mono font-semibold whitespace-nowrap">₹{(o.amount / 100).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${statusColor}`}>{o.status}</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link to={`/vendor/invoices/${o.id}`} target="_blank" className="text-[#2c54b2] hover:text-[#ee5e23] text-xs font-semibold inline-flex items-center gap-1" data-testid={`view-invoice-${o.id}`}>
                          View <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorBillingPanel;
